// ---------------------------------------------------------------------------
// PlatformRestFallback — KgDesignSystemFallback backed by platform.cogo.xyz.
//
// Phase 1b Step 1b.4 per .planning/kg-repository-interface.md v1.0 §7.1.
//
// Studio today calls `GET {platformBase}/api/design-systems` to populate
// the DS picker (fetchPresetsViaWorker pattern). Phase 1b's HybridRepository
// accepts this class as the DS fallback — local WASM is the preferred path,
// but until per-DS shards land (Step 5-6 scope) the Core shard has zero
// DS instances, so HybridRepository MUST fall through here.
//
// Response shape — the Worker's `SELECT_FIELDS` (cogo-cloudflare
// routes/design-systems.ts) is the authority, NOT the abbreviated shape
// Studio's fetch-presets-via-worker.ts happens to consume:
//   [ { id, user_id, name, description, tokens, is_public, … } ]
//
// Extended to DesignSystem by synthesizing the IRI from `id` and projecting
// `tokens` onto `axes`. This comment used to list only `{id, name,
// description}`, which is how the `tokens` drop stayed invisible: the
// mapper looked complete against its own documentation.
// ---------------------------------------------------------------------------

import type { DesignSystem } from "../types"
import type { KgDesignSystemFallback } from "./types"

export interface PlatformRestFallbackConfig {
  /**
   * Base URL, e.g. "https://platform.cogo.xyz". Paths are appended — this
   * class calls `{base}/api/design-systems` and `{base}/api/design-systems/{id}`.
   */
  baseUrl: string
  /** Optional Bearer token supplier. */
  getAuthToken?: () => Promise<string | null | undefined>
  /** Optional static headers for proxy/connectivity layers. */
  headers?: Record<string, string>
  /** Optional fetch override for testing. */
  fetchImpl?: typeof fetch
  /** Abort timeout (ms). Default: 10000. */
  timeoutMs?: number
}

interface PresetListItem {
  id: string
  name: string
  description?: string
  /**
   * The saved axis snapshot, as `design_system_presets.tokens`. Studio's
   * "Save to library" writes the 6-axis design-system state here, and the
   * Worker's `SELECT_FIELDS` returns it on both list and get. Typed
   * `unknown` because the column is free-form JSON — older rows hold other
   * shapes, and it is not this layer's job to adjudicate them.
   */
  tokens?: unknown
}

/** Build a cogo:DesignSystem IRI from the short id. */
function synthIri(id: string): string {
  return `https://cogo.xyz/ns/v1#DesignSystem-${id}`
}

/**
 * Project the persisted `tokens` column onto `DesignSystem.axes`.
 *
 * Dropping this projection was the 2026-08-04 defect: a workspace with 48
 * saved design systems showed 48 rows, every one of them reading "Saved
 * without an axis snapshot" with a disabled "Load in editor", because
 * `axes` was undefined on every row the REST path ever produced. The write
 * path and the Worker were both storing and returning the snapshot; only
 * this mapper failed to carry it across.
 *
 * The projection is deliberately syntactic — it keeps string-valued entries
 * and nothing else. Deciding whether those strings form a *loadable* 6-axis
 * snapshot needs Studio's axis option lists, which this package does not
 * (and should not) import; that judgment lives at the consumer, which is
 * why a partial or foreign payload still ends up honestly non-loadable.
 * `axes` is omitted entirely rather than set to `{}` so a snapshot-less row
 * stays distinguishable from an empty one.
 */
function toAxes(tokens: unknown): Record<string, string> | undefined {
  if (!tokens || typeof tokens !== "object" || Array.isArray(tokens)) {
    return undefined
  }
  const axes: Record<string, string> = {}
  for (const [key, value] of Object.entries(tokens as Record<string, unknown>)) {
    if (typeof value === "string") axes[key] = value
  }
  return Object.keys(axes).length > 0 ? axes : undefined
}

function toDesignSystem(raw: unknown): DesignSystem | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const id = typeof r.id === "string" ? r.id : null
  const name = typeof r.name === "string" ? r.name : null
  if (!id || !name) return null
  const axes = toAxes(r.tokens)
  return { id, iri: synthIri(id), name, ...(axes ? { axes } : {}) }
}

export class PlatformRestFallback implements KgDesignSystemFallback {
  constructor(private readonly config: PlatformRestFallbackConfig) {}

  async listDesignSystems(): Promise<DesignSystem[]> {
    const arr = await this.call<PresetListItem[]>("/api/design-systems")
    if (!Array.isArray(arr)) return []
    return arr.map(toDesignSystem).filter((d): d is DesignSystem => d !== null)
  }

  async getDesignSystem(id: string): Promise<DesignSystem | null> {
    const safe = encodeURIComponent(id)
    const body = await this.call<PresetListItem | null>(
      `/api/design-systems/${safe}`,
    )
    return toDesignSystem(body)
  }

  // ——— Internals ———

  private async call<T>(path: string): Promise<T | null> {
    const fetcher = this.config.fetchImpl ?? fetch
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(this.config.headers ?? {}),
    }
    if (this.config.getAuthToken) {
      const tok = await this.config.getAuthToken()
      if (tok) headers["Authorization"] = `Bearer ${tok}`
    }
    const controller = new AbortController()
    const timer = setTimeout(
      () => controller.abort(),
      this.config.timeoutMs ?? 10000,
    )
    try {
      const res = await fetcher(`${this.config.baseUrl}${path}`, {
        method: "GET",
        headers,
        signal: controller.signal,
      })
      if (res.status === 404) return null
      // Graceful-degrade on auth-gated endpoints when the user isn't
      // authorized. Anonymous clients (no Supabase session) should see
      // an empty result instead of an exception — matches the
      // `fetchPresetsViaWorker` "no token → return []" semantic used
      // everywhere else in Studio. Phase 2b KgSmoke observed 401 throws
      // preventing `HybridRepository.listDesignSystems()` from falling
      // back gracefully; see `.planning/phase-2b-judge-first-consumer-results.md`
      // §5.4 #1.
      if (res.status === 401 || res.status === 403) return null
      if (!res.ok) {
        throw new Error(
          `[kg-repository platform-rest] GET ${path} HTTP ${res.status} ${res.statusText}`,
        )
      }
      return (await res.json()) as T
    } finally {
      clearTimeout(timer)
    }
  }
}
